import { Edit } from '@mui/icons-material';
import { GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';
import { type JSX, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import { UserContext } from '~/auth/userContext';
import { ElevatorPreviewPanel } from '~/components/compliance/ElevatorPreviewPanel';
import { carSearchParam } from '~/components/global/constants';
import { buildCarColumns } from '~/components/portfolio/propertyCarColumns';
import { openInNewTab } from '~/components/rowPreview/RowPreviewPanel';
import { useTableRowPreview } from '~/components/rowPreview/useTableRowPreview';
import { LiftAITable } from '~/components/table';
import { useOEMs } from '~/data/hooks/useOEMs';
import type { CarInProperty } from '~/data/schemas/car';
import { useDetailTableHeight } from '~/hooks/useDetailTableHeight';
import { useGridFilterModel } from '~/hooks/useGridFilterModel';
import { useLocaleTerms } from '~/hooks/useLocaleTerms';
import { usePropertyCarsKpis } from '~/hooks/usePropertyCarsKpis';
import { canAccess, EDIT_ELEVATOR } from '~/utils/role';
import { buildUrlForElevator } from '~/utils/urlBuilder';

import PropertyEditCarDialog from './PropertyEditCarDialog';

export type PropertyCarsProps = { propertyId: string };
export const PropertyCars: React.FC<PropertyCarsProps> = ({ propertyId }) => {
  const tableHeight = useDetailTableHeight();
  const [gridAction, setGridAction] = useState<{
    actionName: 'actionEdit';
    carId: string;
  } | null>(null);

  const [searchParams] = useSearchParams();

  // An elevator has a page of its own, and the name cell links straight to it.
  const carDetailPath = useCallback(
    (carId: string) => buildUrlForElevator(carId, searchParams),
    [searchParams],
  );
  // The whole row is the unit's entry point, not just its name cell — the KPI cells are
  // the reason a reader is scanning the grid. Each of those keeps its own link into the
  // property's Tickets tab; `rowHref` only fires outside them.
  const rowHref = useCallback(
    (car: CarInProperty) => (car.id ? carDetailPath(car.id) : undefined),
    [carDetailPath],
  );
  const { filterModel, setFilterModel } = useGridFilterModel();
  const terms = useLocaleTerms();

  const { cars = [], loading } = usePropertyCarsKpis(propertyId);

  const carId = searchParams.get(carSearchParam) ?? undefined;
  useEffect(() => {
    if (carId) {
      setFilterModel({
        items: [
          {
            field: 'id',
            value: carId,
            operator: 'equals',
          },
        ],
      });
    }
  }, [carId, setFilterModel]);

  const clearGridAction = useCallback(() => {
    setGridAction(null);
  }, []);

  const apiRef = useGridApiRef();
  const preview = useTableRowPreview({
    list: 'property_elevators',
    apiRef,
    canPreview: (rowId) => Boolean(apiRef.current?.getRow<CarInProperty>(rowId)?.id),
  });

  const {
    userDetails: { role: userRole },
  } = useContext(UserContext);
  const canEdit = canAccess(userRole, EDIT_ELEVATOR);
  const { oems } = useOEMs();

  const carToEdit = useMemo(() => {
    if (!gridAction) return null;
    const car = cars.find((car) => car.id === gridAction.carId);
    if (!car) return null;
    return { ...car, oem: oems?.find((oem) => oem.name === car.oem)?.id ?? null };
  }, [gridAction, cars, oems]);

  const getActionsEl = useCallback((car: CarInProperty): JSX.Element[] => {
    return [
      <GridActionsCellItem
        key="edit"
        icon={<Edit />}
        onClick={() => {
          if (!car.id) {
            console.error('Car has no ID');
            return;
          }
          setGridAction({ actionName: 'actionEdit', carId: car.id });
        }}
        label="Edit"
        showInMenu
      />,
    ];
  }, []);

  const carColumns = useMemo(
    () =>
      buildCarColumns({
        propertyId,
        canEdit,
        getActionsEl,
        terms,
        carDetailPath,
      }),
    [propertyId, canEdit, getActionsEl, terms, carDetailPath],
  );

  return (
    <>
      <LiftAITable<CarInProperty>
        height={tableHeight}
        apiRef={apiRef}
        rows={cars}
        loading={loading}
        filterModel={filterModel}
        onFilterModelChange={setFilterModel}
        initialState={{
          columns: {
            columnVisibilityModel: {
              id: false,
              capacityLbs: false,
              controllerModel: false,
              driveType: false,
              lastSspiTest: false,
              numEntrances: false,
              numLandings: false,
              oem: false,
              speedFpm: false,
            },
          },
        }}
        columns={carColumns}
        rowHref={rowHref}
        toolbarProps={{ filterControls: preview.peekSwitch }}
        {...preview.tableProps}
        tableHeaderTitle={terms.vehicles}
        enableQuickFilter
        quickFilterPlaceHolder={`Search ${terms.vehicles.toLowerCase()}...`}
      />
      {preview.enabled ? (
        <ElevatorPreviewPanel
          rowPreview={preview.rowPreview}
          onOpenFullPage={(carId) => openInNewTab(carDetailPath(carId))}
        />
      ) : null}
      {carToEdit && canAccess(userRole, EDIT_ELEVATOR) ? (
        <PropertyEditCarDialog
          open
          onClose={clearGridAction}
          onSubmit={clearGridAction}
          defaultValues={carToEdit}
        />
      ) : null}
    </>
  );
};
